import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Input, Select, Switch } from 'antd';
import {
  CameraOutlined,
  CheckCircleOutlined,
  CloseCircleFilled,
  EnvironmentOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import './AdminUserProfileModal.css';

const generoOptions = [
  { label: 'Masculino', value: 'Masculino' },
  { label: 'Femenino', value: 'Femenino' },
  { label: 'Otro', value: 'Otro' },
];

const buscoOptions = [
  { label: 'Hombres', value: 'Hombres' },
  { label: 'Mujeres', value: 'Mujeres' },
  { label: 'Ambos', value: 'Ambos' },
];

const SwitchCard = ({ label, subLabel, value, onChange }) => (
  <div className="admin-profile-switch-card">
    <div>
      <p className="admin-profile-switch-title">{label}</p>
      <p className="admin-profile-switch-subtitle">{subLabel}</p>
    </div>
    <Switch checked={value} onChange={onChange} />
  </div>
);

const InputRow = ({ icon, label, children }) => (
  <div className="admin-profile-input-row">
    <div className="admin-profile-input-icon">{icon}</div>
    <div className="admin-profile-input-content">
      <p className="admin-profile-input-label">{label}</p>
      {children}
    </div>
  </div>
);

const AdminUserProfileEditor = ({ user, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [photos, setPhotos] = useState(Array(9).fill(null));
  const perfilPublico = Form.useWatch('perfil_publico', form);
  const visibilidadFoto = Form.useWatch('visibilidad_foto', form);
  const privacidadActiva = Form.useWatch('privacidad_activa', form);
  const verificado = Form.useWatch('verificado', form);

  useEffect(() => {
    if (!user) {
      form.resetFields();
      setFeedback(null);
      return;
    }

    form.setFieldsValue({
      genero: user.Perfil?.genero || undefined,
      busco: user.Perfil?.busco || undefined,
      fecha_nacimiento: user.Perfil?.fecha_nacimiento
        ? user.Perfil.fecha_nacimiento.slice(0, 10)
        : undefined,
      direccion: user.Perfil?.direccion || '',
      descripcion: user.Perfil?.descripcion || '',
      perfil_publico: user.Perfil?.perfil_publico ?? false,
      visibilidad_foto: user.Perfil?.visibilidad_foto ?? false,
      privacidad_activa: user.Perfil?.privacidad_activa ?? true,
      verificado: user.Perfil?.verificado ?? false,
    });
    const existingPhotoUrls = user.Perfil?.fotos?.map((foto) => foto.url) || [];
    const nextPhotos = Array(9).fill(null);
    existingPhotoUrls.forEach((url, index) => {
      if (index < 9) nextPhotos[index] = url;
    });
    setPhotos(nextPhotos);
    setFeedback(null);
  }, [form, user]);

  const photoPreviews = useMemo(
    () =>
      photos.map((photo) => {
        if (!photo) return null;
        return photo instanceof File ? URL.createObjectURL(photo) : photo;
      }),
    [photos]
  );

  useEffect(
    () => () => {
      photoPreviews.forEach((preview, index) => {
        if (photos[index] instanceof File && preview) {
          URL.revokeObjectURL(preview);
        }
      });
    },
    [photoPreviews, photos]
  );

  const handleImageChange = (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPhotos((current) => {
      const next = [...current];
      next[index] = file;
      return next;
    });

    event.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos((current) => {
      const next = [...current];
      next[index] = null;
      return next;
    });
  };

  const handleSubmit = async (values) => {
    if (!user?.id) return;

    try {
      setSaving(true);
      setFeedback(null);

      const formData = new FormData();
      const existingPhotos = photos.filter((photo) => typeof photo === 'string');

      photos.forEach((photo) => {
        if (photo instanceof File) {
          formData.append('fotos', photo);
        }
      });

      formData.append('fotosExistentes', JSON.stringify(existingPhotos));
      formData.append('genero', values.genero || '');
      formData.append('busco', values.busco || '');
      formData.append('fecha_nacimiento', values.fecha_nacimiento || '');
      formData.append('direccion', values.direccion || '');
      formData.append('descripcion', values.descripcion || '');
      formData.append('perfil_publico', String(Boolean(values.perfil_publico)));
      formData.append('visibilidad_foto', String(Boolean(values.visibilidad_foto)));
      formData.append('privacidad_activa', String(Boolean(values.privacidad_activa)));
      formData.append('verificado', String(Boolean(values.verificado)));

      const res = await axios.patch(
        `https://app-swingervip-production.up.railway.app/api/admin/users/${user.id}/profile`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setFeedback({
        type: 'success',
        message: res.data.message || 'Perfil actualizado correctamente.',
      });
      onSuccess?.(res.data.usuario);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.response?.data?.message || 'No se pudo actualizar el perfil.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-profile-shell">
      <div className="admin-profile-topbar">
        <button type="button" className="admin-profile-back" onClick={onClose}>
          Cerrar
        </button>
        <h2 className="admin-profile-heading">Editar perfil</h2>
        <button
          type="button"
          className="admin-profile-save-link"
          onClick={() => form.submit()}
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'GUARDAR'}
        </button>
      </div>

      <div className="admin-profile-body">
        <div className="admin-profile-photo-grid">
          {photos.map((photo, index) => {
            const preview = photoPreviews[index];

            return (
              <div key={index} className="admin-profile-photo-slot">
                {preview ? (
                  <>
                    <img
                      src={preview}
                      alt={`Foto ${index + 1}`}
                      className="admin-profile-photo-image"
                    />
                    <button
                      type="button"
                      className="admin-profile-photo-remove"
                      onClick={() => removePhoto(index)}
                    >
                      <CloseCircleFilled />
                    </button>
                  </>
                ) : (
                  <label className="admin-profile-photo-placeholder">
                    <CameraOutlined />
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(event) => handleImageChange(index, event)}
                    />
                  </label>
                )}
              </div>
            );
          })}
        </div>

        <div className="admin-profile-usercard">
          <div className="admin-profile-avatar">{user?.nombre?.charAt(0)}</div>
          <div>
            <h3 className="admin-profile-username">
              {user ? `${user.nombre} ${user.apellido}` : 'Perfil'}
            </h3>
            <p className="admin-profile-useremail">{user?.correo_electronico}</p>
          </div>
        </div>

        {feedback ? (
          <Alert
            style={{ marginBottom: 20 }}
            type={feedback.type}
            message={feedback.message}
            showIcon
          />
        ) : null}

        <Form form={form} onFinish={handleSubmit} layout="vertical" className="admin-profile-form">
          <div className="admin-profile-switch-grid">
            <Form.Item name="perfil_publico" valuePropName="checked" noStyle>
              <SwitchCard
                label="Perfil público"
                subLabel={perfilPublico ? 'Visible' : 'Oculto'}
                value={Boolean(perfilPublico)}
                onChange={(checked) => form.setFieldValue('perfil_publico', checked)}
              />
            </Form.Item>

            <Form.Item name="visibilidad_foto" valuePropName="checked" noStyle>
              <SwitchCard
                label="Visibilidad de foto"
                subLabel={visibilidadFoto ? 'Sí' : 'No'}
                value={Boolean(visibilidadFoto)}
                onChange={(checked) => form.setFieldValue('visibilidad_foto', checked)}
              />
            </Form.Item>

            <Form.Item name="privacidad_activa" valuePropName="checked" noStyle>
              <SwitchCard
                label="Privacidad activa"
                subLabel={privacidadActiva ? 'Activa' : 'Inactiva'}
                value={Boolean(privacidadActiva)}
                onChange={(checked) => form.setFieldValue('privacidad_activa', checked)}
              />
            </Form.Item>

            <Form.Item name="verificado" valuePropName="checked" noStyle>
              <SwitchCard
                label="Verificado"
                subLabel={verificado ? 'Sí' : 'No'}
                value={Boolean(verificado)}
                onChange={(checked) => form.setFieldValue('verificado', checked)}
              />
            </Form.Item>
          </div>

          <div className="admin-profile-card-list">
            <InputRow icon={<UserOutlined />} label="Descripción">
              <Form.Item name="descripcion" noStyle>
                <Input.TextArea
                  rows={4}
                  maxLength={1000}
                  placeholder="Contanos algo sobre este perfil"
                  className="admin-profile-textarea"
                />
              </Form.Item>
            </InputRow>

            <InputRow icon={<CheckCircleOutlined />} label="Género">
              <Form.Item name="genero" noStyle>
                <Select
                  allowClear
                  options={generoOptions}
                  placeholder="Seleccionar género"
                  className="admin-profile-select"
                />
              </Form.Item>
            </InputRow>

            <InputRow icon={<SearchOutlined />} label="Busco">
              <Form.Item name="busco" noStyle>
                <Select
                  allowClear
                  options={buscoOptions}
                  placeholder="Seleccionar búsqueda"
                  className="admin-profile-select"
                />
              </Form.Item>
            </InputRow>

            <InputRow icon={<EnvironmentOutlined />} label="Dirección">
              <Form.Item name="direccion" noStyle>
                <Input placeholder="Agregar dirección" className="admin-profile-input" />
              </Form.Item>
            </InputRow>

            <InputRow icon={<CheckCircleOutlined />} label="Fecha de nacimiento">
              <Form.Item name="fecha_nacimiento" noStyle>
                <Input type="date" className="admin-profile-input" />
              </Form.Item>
            </InputRow>
          </div>

          <div className="admin-profile-footer">
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Guardar perfil
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AdminUserProfileEditor;
